(function () {
  function init(root) {
    if (!root || root.dataset.homeInitialized === "true") return;
    root.dataset.homeInitialized = "true";

    const viewer = root.querySelector("[data-learning-viewer]");
    if (!viewer) return;

    const topicTriggers = Array.from(
      viewer.querySelectorAll("[data-learning-topic]"),
    );
    const topicCards = Array.from(
      viewer.querySelectorAll(
        "[data-learning-topic-card], .home-learning-topic",
      ),
    );
    const scenes = Array.from(viewer.querySelectorAll("[data-learning-scene]"));
    const mobileScenes = Array.from(
      viewer.querySelectorAll("[data-learning-mobile-scene]"),
    );
    const images = Array.from(viewer.querySelectorAll("[data-learning-image]"));
    const desktopList = viewer.querySelector(
      "[data-learning-desktop-list], .home-learning-topic-rail",
    );
    const close = viewer.querySelector("[data-learning-close]");
    const previous = viewer.querySelector(".home-learning-nav-button--prev");
    const next = viewer.querySelector(".home-learning-nav-button--next");
    const mobilePrevious = viewer.querySelector("[data-learning-mobile-prev]");
    const mobileNext = viewer.querySelector("[data-learning-mobile-next]");
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let activeIndex = null;
    let centerActiveTimer = null;
    let centerActiveAnimation = null;

    function cancelActiveCardAnimation() {
      if (centerActiveAnimation !== null) {
        window.cancelAnimationFrame(centerActiveAnimation);
        centerActiveAnimation = null;
      }
    }

    function centerActiveCard() {
      if (
        !desktopList ||
        activeIndex === null ||
        !window.matchMedia("(min-width: 768px)").matches
      ) {
        return;
      }

      const card = topicCards[activeIndex];
      if (!card) return;

      const listRect = desktopList.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const targetTop =
        desktopList.scrollTop +
        cardRect.top -
        listRect.top -
        (desktopList.clientHeight - cardRect.height) / 2;
      const maxScrollTop = Math.max(
        0,
        desktopList.scrollHeight - desktopList.clientHeight,
      );
      const nextScrollTop = Math.min(maxScrollTop, Math.max(0, targetTop));
      const currentScrollTop = desktopList.scrollTop;

      cancelActiveCardAnimation();
      if (reducedMotion || Math.abs(nextScrollTop - currentScrollTop) < 1) {
        desktopList.scrollTo({ top: nextScrollTop, behavior: "auto" });
        return;
      }

      const startedAt = performance.now();
      const duration = 280;
      const animate = (timestamp) => {
        const progress = Math.min(1, (timestamp - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        desktopList.scrollTop =
          currentScrollTop + (nextScrollTop - currentScrollTop) * eased;

        if (progress < 1) {
          centerActiveAnimation = window.requestAnimationFrame(animate);
        } else {
          centerActiveAnimation = null;
        }
      };

      centerActiveAnimation = window.requestAnimationFrame(animate);
    }

    function scheduleActiveCardCentering() {
      if (!desktopList || !window.matchMedia("(min-width: 768px)").matches) {
        return;
      }

      window.clearTimeout(centerActiveTimer);
      cancelActiveCardAnimation();
      centerActiveTimer = window.setTimeout(
        centerActiveCard,
        reducedMotion ? 0 : 760,
      );
    }

    function setActive(index, moveFocus) {
      if (!topicCards.length) return;
      activeIndex = (index + topicCards.length) % topicCards.length;
      viewer.classList.remove("is-closed");

      topicCards.forEach((card, cardIndex) =>
        card.classList.toggle("is-active", cardIndex === activeIndex),
      );
      topicTriggers.forEach((trigger) => {
        const active = Number(trigger.dataset.learningTopic) === activeIndex;
        trigger.setAttribute("aria-expanded", String(active));
      });
      scenes.forEach((scene) => {
        const active = Number(scene.dataset.learningScene) === activeIndex;
        scene.hidden = false;
        scene.setAttribute("aria-hidden", String(!active));
        scene.classList.toggle("is-active", active);
      });
      mobileScenes.forEach(
        (scene) =>
          (scene.hidden =
            Number(scene.dataset.learningMobileScene) !== activeIndex),
      );
      images.forEach((image) =>
        image.classList.toggle(
          "is-active",
          Number(image.dataset.learningImage) === activeIndex,
        ),
      );

      scheduleActiveCardCentering();
      if (moveFocus) {
        const trigger = topicTriggers.find(
          (item) => Number(item.dataset.learningTopic) === activeIndex,
        );
        trigger?.focus({ preventScroll: true });
      }
    }

    function closeActive() {
      activeIndex = null;
      window.clearTimeout(centerActiveTimer);
      cancelActiveCardAnimation();
      viewer.classList.add("is-closed");
      topicCards.forEach((card) => card.classList.remove("is-active"));
      topicTriggers.forEach((trigger) =>
        trigger.setAttribute("aria-expanded", "false"),
      );
      scenes.forEach((scene) => {
        scene.hidden = false;
        scene.setAttribute("aria-hidden", "true");
        scene.classList.remove("is-active");
      });
      mobileScenes.forEach((scene) => (scene.hidden = true));
      images.forEach((image) =>
        image.classList.toggle(
          "is-active",
          image.dataset.learningImage === "closed",
        ),
      );
      if (desktopList)
        desktopList.scrollTo({
          top: 0,
          behavior: reducedMotion ? "auto" : "smooth",
        });
    }

    topicTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () =>
        setActive(Number(trigger.dataset.learningTopic), false),
      );
      trigger.addEventListener("keydown", (event) => {
        const index = Number(trigger.dataset.learningTopic);
        const keyMap = {
          ArrowUp: index - 1,
          ArrowDown: index + 1,
          ArrowLeft: index - 1,
          ArrowRight: index + 1,
          Home: 0,
          End: topicCards.length - 1,
        };
        if (!(event.key in keyMap)) return;
        event.preventDefault();
        setActive(keyMap[event.key], true);
      });
    });

    previous?.addEventListener("click", () =>
      setActive((activeIndex ?? 0) - 1, true),
    );
    next?.addEventListener("click", () =>
      setActive((activeIndex ?? -1) + 1, true),
    );
    mobilePrevious?.addEventListener("click", () =>
      setActive((activeIndex ?? 0) - 1, false),
    );
    mobileNext?.addEventListener("click", () =>
      setActive((activeIndex ?? -1) + 1, false),
    );
    close?.addEventListener("click", closeActive);
    viewer.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && activeIndex !== null) closeActive();
    });
    closeActive();
  }

  function initAll(scope) {
    scope.querySelectorAll("[data-home-authoritative]").forEach(init);
  }

  initAll(document);
  document.addEventListener("shopify:section:load", (event) =>
    initAll(event.target),
  );
})();
